import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scrolls to the element named by the URL hash after a navigation.
 *
 * react-router does not scroll to hashes, and on a cold load of `/#contact` the
 * target element does not exist yet for the browser's own jump. Bare
 * `scrollIntoView()` inherits `html { scroll-behavior }` from `index.css`, which
 * the reduced-motion block already flips to `auto`, and honours `scroll-mt-*`.
 *
 * Known limitation: this also fires on a Back/Forward that lands on a hash URL,
 * overriding the scroll position the browser restored for that entry. Guarding on
 * `useNavigationType() === Pop` does NOT fix it — react-router initialises its
 * action to POP, so that guard would silently break the cold-load case above (the
 * primary path, since `/contact` 301s to `/#contact` as a fresh load). Entries
 * created by native anchors also carry no router state, so `location.key` is
 * `"default"` for them too and can't discriminate either. Accepted as-is.
 */
const ScrollToHash = (): null => {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return undefined;

    // Deferred a frame so the target is mounted regardless of sibling effect order.
    const frame = requestAnimationFrame(() => {
      document.getElementById(hash.slice(1))?.scrollIntoView();
    });

    return () => cancelAnimationFrame(frame);
  }, [hash]);

  return null;
};

export default ScrollToHash;
